import { Component } from '@theme/component';

/**
 * A custom element that displays a live clock with date and time.
 * Updates every second and formats according to user's locale.
 *
 * @extends {Component}
 */
class ClockComponent extends Component {
  /**
   * @type {number | null}
   */
  #intervalId = null;

  /**
   * @type {Intl.DateTimeFormat}
   */
  #dateFormatter;

  /**
   * @type {Intl.DateTimeFormat}
   */
  #timeFormatter;

  constructor() {
    super();
    
    // Initialize formatters for better performance - force CST timezone
    this.#dateFormatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Chicago'
    });

    this.#timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: 'America/Chicago'
    });
  }

  /**
   * Updates the clock display with current date and time
   */
  #updateClock = () => {
    const now = new Date();
    const dateString = this.#dateFormatter.format(now);
    const timeString = this.#timeFormatter.format(now);
    
    this.textContent = `${dateString} ${timeString}`;
  };

  /**
   * Starts the clock interval
   */
  #startClock() {
    // Update immediately
    this.#updateClock();
    
    // Then update every second
    this.#intervalId = setInterval(this.#updateClock, 1000);
  }

  /**
   * Stops the clock interval
   */
  #stopClock() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#startClock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopClock();
  }
}

if (!customElements.get('clock-component')) {
  customElements.define('clock-component', ClockComponent);
}
